import type { FormField, FormSchema } from './form-schema.js';

/**
 * What an assignment can execute, as a free-form token. There is no fixed
 * vocabulary: a qurbani season sorts its dormitories by `urgent` and
 * `large_animal`, a recitation season by which one it can host, a well by the
 * rig it has, and no enum covers the three. The shape is an identifier's,
 * because a tag **is** the `value` of one of the campaign's own select options
 * and an option value is code while its `label` is the Turkish an operator
 * reads (.claude/rules/i18n-turkish-ui.md).
 */
export const CAPABILITY_TAG_PATTERN = /^[a-z][a-z0-9_]{0,59}$/;

/**
 * How many of its own fields a campaign may let decide. Each named key is one
 * more `AND` over the same tag set, so a campaign needing more than this is
 * describing a different kind of institution rather than filtering one.
 */
export const MAX_CAPABILITY_FIELD_KEYS = 8;

/** The field types whose stored value is an option value, so a tag can equal it. */
export const CAPABILITY_FIELD_TYPES = ['select', 'radio', 'multiselect'] as const;

export type CapabilityFieldType = (typeof CAPABILITY_FIELD_TYPES)[number];

export function isCapabilityField(
  field: Pick<FormField, 'type'>,
): field is Extract<FormField, { type: CapabilityFieldType }> {
  return (CAPABILITY_FIELD_TYPES as readonly string[]).includes(field.type);
}

/** The keys of the fields a campaign may name, in schema order. */
export function capabilityFieldCandidates(
  schema: FormSchema,
): Extract<FormField, { type: CapabilityFieldType }>[] {
  return schema.fields.filter(isCapabilityField);
}

/**
 * The first option value of a field that no capability tag could ever equal,
 * or null when every one could. Eligibility compares an answer with a tag by
 * exact equality and a tag must match `CAPABILITY_TAG_PATTERN`, so a field
 * answering `SO` would leave **no** yurt eligible the moment a clerk answered
 * it, and the donation could not be saved.
 */
export function untaggableOption(field: {
  readonly options?: readonly { readonly value: string }[];
}): string | null {
  const option = (field.options ?? []).find(({ value }) => !CAPABILITY_TAG_PATTERN.test(value));
  return option?.value ?? null;
}

/** Why a campaign may not name these keys against this published schema. */
export type CapabilityFieldKeyProblem =
  | { readonly problem: 'field_unknown'; readonly fieldKey: string }
  | { readonly problem: 'option_not_a_tag'; readonly fieldKey: string; readonly option: string };

/**
 * The rule the trigger `campaigns_capability_keys_name_a_published_field`
 * holds, asked first so a refusal names the key and the option rather than a
 * trigger: every named key is a published `select`, `radio` or `multiselect`,
 * and every option value of it has a tag's shape. It is asked when the keys are
 * set and when a schema is published, because either can break it.
 */
export function capabilityFieldKeyProblem(
  keys: readonly string[],
  schema: FormSchema,
): CapabilityFieldKeyProblem | null {
  const candidates = new Map(capabilityFieldCandidates(schema).map((field) => [field.key, field]));
  for (const fieldKey of keys) {
    const field = candidates.get(fieldKey);
    if (!field) return { problem: 'field_unknown', fieldKey };
    const option = untaggableOption(field);
    if (option !== null) return { problem: 'option_not_a_tag', fieldKey, option };
  }
  return null;
}

/**
 * `donations.form_data` is untyped `jsonb`, so it reaches here as `unknown` and
 * is read leniently — the same way the renderer and the compiler read a document
 * captured under an older schema version.
 */
function answersOf(formData: unknown): Readonly<Record<string, unknown>> {
  if (formData === null || typeof formData !== 'object' || Array.isArray(formData)) return {};
  return formData as Record<string, unknown>;
}

/** The requirement an assignment failed: which answer asked for which tag. */
export type CapabilityRequirement = { fieldKey: string; tag: string };

function requiredTags(value: unknown): string[] {
  if (typeof value === 'string') return value === '' ? [] : [value];
  if (Array.isArray(value))
    return value.filter((entry): entry is string => typeof entry === 'string');
  return [];
}

/**
 * The one eligibility rule, pure and shared by the read that offers yurts and
 * the write that refuses one: an assignment may execute a donation when its
 * capability set **contains the value of every field the campaign names**.
 *
 * It branches on nothing. A campaign that names no keys has no requirement, so
 * every active assignment is eligible — exactly what every campaign did before
 * capabilities existed. An unanswered field asks for nothing either, so the
 * yurt list narrows as the clerk answers rather than starting empty.
 *
 * A named key is a `select`, a `radio` or a `multiselect`, so its value is an
 * option value or a list of them; the database refuses a campaign naming any
 * other field, which is why anything else here is no requirement rather than a
 * guess at one.
 */
export function unsatisfiedCapability(
  fieldKeys: readonly string[],
  capabilities: readonly string[],
  formData: unknown,
): CapabilityRequirement | null {
  const answers = answersOf(formData);
  for (const fieldKey of fieldKeys) {
    for (const tag of requiredTags(answers[fieldKey])) {
      if (!capabilities.includes(tag)) return { fieldKey, tag };
    }
  }
  return null;
}

export function satisfiesCapabilities(
  fieldKeys: readonly string[],
  capabilities: readonly string[],
  formData: unknown,
): boolean {
  return unsatisfiedCapability(fieldKeys, capabilities, formData) === null;
}
